import { Stack, TextField, Typography } from "@material-ui/core";
import { CustomNumericFormat } from "./CustomNumericFormat";
import { OwnersPicker } from "../../table/filters/OwnersPicker";
import { BuyerSellerSide } from "./BuyerSellerSide";
import { LeaseTermSection } from "./LeaseTermSection";
import { useTranslation } from "react-i18next";
import { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import type { EditableListing } from "../../../../../@types/listings/new";
import type { AdditionalUser, AdditionalUserWithFee } from "../../../../../@types/listings/user";
import type { LeaseDatesPatch } from "./LeaseTermSection";

type CloseListingDealProps = {
  loading: boolean;
  ownersData: AdditionalUserWithFee[];
  buyerOriginData: AdditionalUserWithFee[];
  organizationUsers: AdditionalUser[];
  editableFormColumns: EditableListing;
  /**
   * A rent listing whose deal is being — or was — closed, i.e. "הושכר". Decided by the
   * dialog, not read off `editableFormColumns.status`: on the row menu's "הושכר" the form
   * still holds the OLD status, and the new one is applied only on save.
   */
  isClosingAsRented: boolean;
  /** The "congratulate on the entry" reminder, after both sides of the deal and before its details. */
  entryCongrats?: ReactNode;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
  onLeaseChange: (lease: LeaseDatesPatch) => void;
  onOwnersDataChange: Dispatch<SetStateAction<AdditionalUserWithFee[]>>;
  onBuyerOriginDataChange: Dispatch<SetStateAction<AdditionalUserWithFee[]>>;
};

export function CloseListingDeal({
  loading,
  ownersData,
  buyerOriginData,
  organizationUsers,
  editableFormColumns,
  isClosingAsRented,
  entryCongrats,
  handleChange,
  onLeaseChange,
  onOwnersDataChange,
  onBuyerOriginDataChange
}: CloseListingDealProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleBuyerOriginChange = (newBuyerOrigin: null | AdditionalUserWithFee[]): void => {
    const newBuyerOriginData: AdditionalUserWithFee[] = newBuyerOrigin.map((item) => {
      if (typeof item === "string") {
        const mappedUserData = organizationUsers.find((user) => user.email === item);

        return {
          ...mappedUserData,
          fee: null,
          email: item,
          finalFee: null,
          feeIncludesTaxes: false
        };
      }

      return item;
    });

    onBuyerOriginDataChange(newBuyerOriginData);
  };

  const { status, signDate, finalListingPrice, dealAdditionDetails, leaseStartDate, leaseEndDate } =
    editableFormColumns;

  const isListingStatusSold = status === "sold";

  // The form keeps its dates as `YYYY-MM-DD` strings under Timestamp-typed fields (see
  // `setInitialListing`); the lease block works on those strings.
  const asDay = (date: unknown): string | null => (typeof date === "string" && date ? date : null);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Typography variant="h5">{translate("listingsPage.closingDeal")}</Typography>
        <Stack gap={2} direction={{ xs: "column", sm: "row" }}>
          <TextField
            fullWidth
            required
            value={finalListingPrice}
            name="finalListingPrice"
            label={translate("listingsPage.tableColumns.finalListingPrice")}
            disabled={loading}
            InputProps={{
              inputComponent: CustomNumericFormat as never
            }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="date"
            name="signDate"
            label={translate("listingsPage.tableColumns.signDate")}
            value={signDate}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
          />
        </Stack>
      </Stack>
      {isClosingAsRented && (
        <LeaseTermSection
          loading={loading}
          leaseStartDate={asDay(leaseStartDate)}
          leaseEndDate={asDay(leaseEndDate)}
          fallbackStartDay={asDay(signDate)}
          onChange={onLeaseChange}
        />
      )}
      <BuyerSellerSide
        side="seller"
        loading={loading}
        personsFeeData={ownersData}
        organizationUsers={organizationUsers}
        editableFormColumns={editableFormColumns}
        handleChange={handleChange}
        onPersonsFeeDataChange={onOwnersDataChange}
      >
        <OwnersPicker
          type="new"
          required
          loading={loading}
          ownersData={ownersData}
          organizationUsers={organizationUsers}
          onOwnersChange={onOwnersDataChange}
        />
      </BuyerSellerSide>
      <BuyerSellerSide
        side="buyer"
        loading={loading}
        personsFeeData={buyerOriginData}
        organizationUsers={organizationUsers}
        editableFormColumns={editableFormColumns}
        handleChange={handleChange}
        onPersonsFeeDataChange={onBuyerOriginDataChange}
      >
        <OwnersPicker
          type="close"
          loading={loading}
          required={isListingStatusSold}
          ownersData={buyerOriginData}
          organizationUsers={organizationUsers}
          onOwnersChange={handleBuyerOriginChange}
        />
      </BuyerSellerSide>
      {entryCongrats}
      <Stack gap={2}>
        <Typography variant="h5">
          {translate("listingsPage.tableColumns.dealAdditionalDetails")}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          name="dealAdditionDetails"
          value={dealAdditionDetails}
          disabled={loading}
          label={translate("listingsPage.tableColumns.dealAdditionalDetails")}
          onChange={handleChange}
        />
      </Stack>
    </Stack>
  );
}
